import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { authService } from '../../services/firebase/auth.service';
import { employeesService } from '../../services/firebase/employees.service';
import { invitationsService } from '../../services/firebase/invitations.service';
import { UserRole } from '../../types/organization.types';

const signupSchema = z
  .object({
    displayName: z.string().min(2, 'Name must be at least 2 characters'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SignupFormData = z.infer<typeof signupSchema>;

interface InvitationSignupFormProps {
  email: string;
  organizationId: string;
  organizationName: string;
  role: UserRole;
  employeeId?: string;
  invitationId: string;
  onComplete: () => void;
}

export default function InvitationSignupForm({
  email,
  organizationId,
  organizationName,
  role,
  employeeId,
  invitationId,
  onComplete,
}: InvitationSignupFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    try {
      setError('');
      setIsLoading(true);

      // Create user account via invitation
      const firebaseUser = await authService.signupViaInvitation(
        email,
        data.password,
        data.displayName,
        organizationId,
        role,
        employeeId
      );

      // If employee invitation, link employee to user
      if (employeeId) {
        await employeesService.linkEmployeeToUser(organizationId, employeeId, firebaseUser.uid);
      }

      // Accept the invitation
      await invitationsService.acceptInvitation(organizationId, invitationId, firebaseUser.uid);

      enqueueSnackbar('Account created! Please verify your email.', { variant: 'success' });
      onComplete();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create account';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="h6" gutterBottom align="center">
        Create Your Account
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Complete your registration to join {organizationName}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email (read-only) */}
      <TextField
        label="Email Address"
        type="email"
        fullWidth
        margin="normal"
        value={email}
        disabled
        InputProps={{
          readOnly: true,
        }}
        helperText="This email is linked to your invitation"
      />

      {/* Display Name */}
      <TextField
        {...register('displayName')}
        label="Full Name"
        type="text"
        fullWidth
        margin="normal"
        autoFocus
        error={!!errors.displayName}
        helperText={errors.displayName?.message}
        disabled={isLoading}
      />

      {/* Password */}
      <TextField
        {...register('password')}
        label="Password"
        type={showPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        autoComplete="new-password"
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Confirm Password */}
      <TextField
        {...register('confirmPassword')}
        label="Confirm Password"
        type={showConfirmPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        autoComplete="new-password"
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3 }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Create Account'}
      </Button>
    </Box>
  );
}
